import './ProcessingModal.css';

import { useEffect, useMemo, useState } from 'react';

import InstallButton from './InstallButton';

const ProcessingModal = ({ isVisible, streamingStatus, streamingProgress, onSequenceComplete }) => {
  const [visibleMessages, setVisibleMessages] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const [sequenceProgress, setSequenceProgress] = useState(0);
  const [messageStates, setMessageStates] = useState([]);

  const processingMessages = useMemo(
    () => [
      { icon: '📖', text: 'Reading your menu...' },
      { icon: '🔍', text: 'Analyzing menu items...' },
      { icon: '🎨', text: 'Generating beautiful images...' },
      { icon: '📊', text: 'Calculating nutritional information...' },
      { icon: '🥗', text: 'Identifying dietary preferences...' },
      { icon: '✨', text: 'Adding finishing touches...' },
      { icon: '🍽️', text: 'Almost ready to serve...' },
    ],
    []
  );

  // Initialize start time and begin message sequence
  useEffect(() => {
    if (isVisible && !startTime) {
      setStartTime(Date.now());
      setVisibleMessages([]);
      setSequenceProgress(0);
      setMessageStates([]);
    } else if (!isVisible) {
      // Reset when modal becomes invisible
      setStartTime(null);
      setVisibleMessages([]);
      setSequenceProgress(0);
      setMessageStates([]);
    }
  }, [isVisible, startTime]);

  // Show messages sequentially in first 7 seconds
  useEffect(() => {
    if (!isVisible || !startTime) return;

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const messageIndex = Math.floor(elapsed / 1200); // Show new message every 1200ms

      // Update sequence progress (0-100% over 7 seconds)
      const progress = Math.min((elapsed / 7000) * 100, 100);
      setSequenceProgress(progress);

      if (elapsed < 7000) {
        // First 7 seconds: show messages sequentially
        if (messageIndex < processingMessages.length) {
          setVisibleMessages(prev => {
            const newMessages = processingMessages.slice(0, messageIndex + 1);
            // Only update if the array is actually different
            if (prev.length !== newMessages.length) {
              return newMessages;
            }
            return prev;
          });

          // Update message states with loading and completion
          setMessageStates(prev => {
            const newStates = [...prev];
            const currentMessageTime = elapsed % 1200;

            // Set states for all visible messages
            for (let i = 0; i <= messageIndex && i < processingMessages.length; i++) {
              if (i === messageIndex) {
                // Current message: show loading for first 800ms, then complete
                if (currentMessageTime < 800) {
                  newStates[i] = 'loading';
                } else {
                  newStates[i] = 'completed';
                }
              } else if (i < messageIndex) {
                // Previous messages: completed
                newStates[i] = 'completed';
              }
            }

            return newStates;
          });
        }
      } else {
        // Sequence completed - call the callback
        if (onSequenceComplete) {
          onSequenceComplete();
        }
      }
    }, 50); // Faster interval for smoother animations

    return () => clearInterval(interval);
  }, [isVisible, startTime, processingMessages, onSequenceComplete]);

  if (!isVisible) return null;

  return (
    <div className="processing-modal-overlay">
      <div className="processing-modal">
        <div className="processing-messages-container">
          <div className="processing-messages-list">
            <span>
              Install the app clicking <InstallButton />
            </span>
            {visibleMessages.map((message, index) => {
              const messageState = messageStates[index] || 'loading';

              return (
                <div key={`${message.icon}-${index}`} className={`processing-message-item ${messageState}`}>
                  <span className="processing-message-icon">{message.icon}</span>
                  <span className="processing-message-text">{message.text}</span>
                  <div className="message-status">
                    {messageState === 'loading' ? (
                      <div className="message-spinner"></div>
                    ) : (
                      <div className="message-checkmark">✓</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="processing-progress-bar">
            <div className="processing-progress-fill" style={{ width: `${sequenceProgress}%` }}></div>
          </div>
          <div className="processing-percentage">{Math.round(sequenceProgress)}%</div>
        </div>
      </div>
    </div>
  );
};

export default ProcessingModal;
